import type { SelectroInlineItem } from '@aha-app/selectro-v2';
import Filter from './Filter';

/**
 * Base class for defining workflow board filters.
 *
 * Handles most filter management. Subclasses should only need to
 * override `matches`.
 *
 * Supports a core data type of [string, T][].
 *
 * While enabling additional metadata to be associated with each item
 * designed to work with the Selectro component.
 */

export type FilterOption<T = string> = {
  text: string;
  value: T;
  label?: string | undefined;
  meta?: Record<string, unknown>;
};

class ItemFilter<T extends string | number> extends Filter<[string, T][]> {
  selectedItems: Set<T>;
  filterOptions: {};
  protected internalOptions: Record<string, FilterOption<T>> = {};

  /**
   * @param initialItems IDs of items initially selected by the filter
   * @param initialOptions [name, id] pairs, or a FilterOption for each item in initialItems
   * @param filterOptions filter options to customize the behavior of the filter
   */
  constructor(
    initialItems: T[],
    initialOptions: ([string, T] | FilterOption<T>)[] = [],
    filterOptions: {} = {}
  ) {
    super();
    this.selectedItems = new Set(initialItems);
    this.setOptions(initialOptions);
    this.filterOptions = filterOptions;
  }

  /**
   * @param id
   */
  addItem(id: T) {
    this.selectedItems.add(id);
  }

  /**
   * @param id
   */
  deleteItem(id: T) {
    this.selectedItems.delete(id);
  }

  /**
   * @param items
   */
  set(items: T[]) {
    this.clearItems();
    items.forEach(item => this.addItem(item));
  }

  /**
   * @param items
   */
  setItems(items: T[]) {
    this.clearItems();
    items.forEach(item => this.addItem(item));
  }

  /**
   * @param id
   */
  itemIsSelected(id: T) {
    // @ts-expect-error not sure why ''
    return this.selectedItems.has(id || '');
  }

  clearItems() {
    this.selectedItems.clear();
  }

  single(): T | undefined {
    const [first] = this.selectedItems;
    return first;
  }

  /**
   * Return the filter data to put in the bookmark config
   */
  toBookmarkConfiguration() {
    return Array.from(this.selectedItems);
  }

  addOption(option: FilterOption<T>) {
    this.internalOptions[String(option.value)] = option;
  }

  /**
   * Updates the set of all possible [name, id] pairs allowed by this
   * filter.
   */
  setOptions(options: ([string, T] | FilterOption<T>)[]) {
    options.forEach(option => {
      if (Array.isArray(option)) {
        const [text, value] = option;
        this.addOption({ value, text });
      } else {
        this.addOption(option);
      }
    });
  }

  /**
   * Returns all selected items in a form that can be passed into
   * SelectroV2.
   */
  selectroSelected(): SelectroInlineItem[] {
    return Array.from(this.selectedItems)
      .map(item => this.internalOptions[String(item)])
      .filter(f => !!f)
      .map(option => ({
        ...option,
        value: String(option.value),
      }));
  }

  /*
   * Return all possible items in a form that can be passed into SelectroV2.
   */
  selectroItems(): SelectroInlineItem[] {
    return Object.values(this.internalOptions).map(option => {
      const { meta, ...rest } = option;
      return {
        ...meta,
        ...rest,
        value: String(option.value),
      };
    });
  }

  selectroSearchFields(): string[] {
    return ['text', 'label'];
  }

  /**
   * Returns the value(s) in a tuple format.
   * this meets the parent Filter class interface
   */
  getValue(): [string, T][] {
    return this.getValues().map(option => [option.text, option.value] as const);
  }

  /**
   * Returns the values selected
   *
   */
  getValues(): FilterOption<T>[] {
    return Array.from(this.selectedItems)
      .map(item => this.internalOptions[String(item)])
      .filter(Boolean);
  }

  get hasItems() {
    return this.selectedItems.size > 0;
  }

  get hasValue() {
    return this.hasItems;
  }

  /**
   * Returns true if the record is matched by this filter, false
   * otherwise. The default is to match all records if no
   * selectedItems are defined.
   */
  matches(record: any) {
    return !this.hasItems;
  }

  /**
   * Return all possible options in a form that can be passed into Selectro.
   * @deprecated Used in v1 Selectro, use selectroItems for SelectroV2
   */
  selectroOptions(): any {
    return Object.values(this.internalOptions).map(option => [
      option.text,
      option.value,
    ]);
  }

  /**
   * Returns all selected items in a form that can be passed into
   * Selectro.
   * @deprecated Used in v1 Selectro, use selectroSelected for SelectroV2
   */
  asOptions() {
    return this.getValue();
  }

  /**
   * Return the key/value pairs for all options.
   * @deprecated Legacy internal option shape
   */
  get allOptions(): Record<string, [string, T]> {
    return Object.fromEntries(
      Object.entries(this.internalOptions).map(([key, option]) => [
        key,
        [option.text, option.value],
      ])
    );
  }
}

export default ItemFilter;
